/**
 * Public Report page — clients open this via the signed share link from the email.
 * NO login required. NO sidebar. NO ModuleSwitcher.
 *
 * Shows:
 *   - Project header
 *   - Inline PDF viewer (iframe to /api/public/report/:token)
 *   - "Approve" and "Comment" actions that POST to /api/public/report/:token/feedback
 *   - List of recent feedback (approvals/comments) for this token
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, MessageSquare, Loader2, ShieldCheck, Clock, FileText } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicReport() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState(null); // "approve" | "comment"
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/report/${token}/meta`);
        setMeta(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Link inválido o expirado");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const es = (meta?.language || "en").startsWith("es");

  const submit = async () => {
    if (!mode) return;
    if (mode === "approve" && !form.name) {
      alert(es ? "Por favor ingresa tu nombre para aprobar." : "Please enter your name to approve.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/report/${token}/feedback`, {
        action: mode,
        message: form.message,
        signatory_name: form.name,
        signatory_email: form.email,
      });
      setDone(true);
      // refresh meta
      const r = await axios.get(`${API}/public/report/${token}/meta`);
      setMeta(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <Loader2 className="animate-spin text-[#003366]" size={28} />
    </div>;
  }

  if (error) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm" data-testid="public-error">
        <div className="w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
          <Clock size={24} />
        </div>
        <h1 className="text-xl font-semibold text-slate-900 mb-2">Link no disponible</h1>
        <p className="text-sm text-slate-600">{error}</p>
        <p className="mt-4 text-xs font-mono text-slate-400">
          Contacta al remitente para solicitar un nuevo enlace.
        </p>
      </div>
    </div>;
  }

  const project = meta?.project || {};
  const pdfUrl = `${API}/public/report/${token}`;
  const feedback = meta?.feedback || [];
  const hasApproval = feedback.some(f => f.action === "approve");

  return (
    <div className="min-h-screen bg-slate-100" data-testid="public-report-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#003366] text-white flex items-center justify-center font-bold">IQ</div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">IQES · IQ-APP</div>
              <div className="font-semibold text-slate-900">{es ? "Reporte de Análisis" : "Analysis Report"}</div>
            </div>
          </div>
          {hasApproval ? (
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wider" data-testid="status-approved">
              <ShieldCheck size={13} /> {es ? "Aprobado" : "Approved"}
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wider">
              <Clock size={13} /> {es ? "Pendiente de revisión" : "Pending review"}
            </div>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 grid lg:grid-cols-[1fr_320px] gap-6">
        {/* PDF column */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="public-pdf-card">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900">{project.name || "—"}</div>
              <div className="text-xs font-mono text-slate-500">
                {project.company || "—"} · {project.location || "—"}
              </div>
            </div>
            <a href={pdfUrl} target="_blank" rel="noreferrer"
               data-testid="public-download-pdf"
               className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline">
              <FileText size={12} /> {es ? "Descargar PDF" : "Download PDF"}
            </a>
          </div>
          <iframe src={pdfUrl} title="Report PDF"
                  className="w-full h-[78vh] bg-slate-50"
                  data-testid="public-pdf-iframe" />
        </div>

        {/* Side panel */}
        <aside className="space-y-4">
          {!done ? (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="public-actions-card">
              <h3 className="font-semibold text-slate-900 mb-1">
                {es ? "Tu revisión" : "Your review"}
              </h3>
              <p className="text-xs text-slate-500 mb-4">
                {es ? "Puedes aprobar el reporte o dejar un comentario. Recibiremos tu respuesta al instante."
                    : "You can approve the report or leave a comment. We'll receive your response immediately."}
              </p>
              <div className="flex gap-2 mb-3">
                <button onClick={() => setMode("approve")}
                        data-testid="btn-mode-approve"
                        className={`flex-1 text-xs font-mono uppercase tracking-wider rounded-xl px-3 py-2 border transition-colors inline-flex items-center justify-center gap-1.5 ${
                          mode === "approve" ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-700 hover:bg-slate-50"
                        }`}>
                  <CheckCircle2 size={12} /> {es ? "Aprobar" : "Approve"}
                </button>
                <button onClick={() => setMode("comment")}
                        data-testid="btn-mode-comment"
                        className={`flex-1 text-xs font-mono uppercase tracking-wider rounded-xl px-3 py-2 border transition-colors inline-flex items-center justify-center gap-1.5 ${
                          mode === "comment" ? "bg-[#003366] text-white border-[#003366]" : "border-slate-300 text-slate-700 hover:bg-slate-50"
                        }`}>
                  <MessageSquare size={12} /> {es ? "Comentar" : "Comment"}
                </button>
              </div>
              {mode && (
                <div className="space-y-2" data-testid="public-form">
                  <input data-testid="public-name" placeholder={es?"Nombre":"Name"}
                         value={form.name}
                         onChange={e => setForm({...form, name: e.target.value})}
                         className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm" />
                  <input data-testid="public-email" placeholder="E-mail" type="email"
                         value={form.email}
                         onChange={e => setForm({...form, email: e.target.value})}
                         className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm" />
                  <textarea data-testid="public-message"
                            placeholder={es?"Mensaje (opcional si apruebas)":"Message (optional if you approve)"}
                            rows={4} value={form.message}
                            onChange={e => setForm({...form, message: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm resize-none" />
                  <button onClick={submit} disabled={submitting}
                          data-testid="public-submit"
                          className={`w-full text-sm font-medium rounded-xl px-4 py-2.5 text-white transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 ${
                            mode === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#003366] hover:bg-[#002244]"
                          }`}>
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : (mode === "approve" ? <CheckCircle2 size={14} /> : <MessageSquare size={14} />)}
                    {mode === "approve"
                      ? (es ? "Confirmar aprobación" : "Confirm approval")
                      : (es ? "Enviar comentario" : "Send comment")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl p-5 text-center" data-testid="public-done">
              <CheckCircle2 size={28} className="mx-auto mb-2" />
              <div className="font-semibold">{es ? "¡Gracias!" : "Thank you!"}</div>
              <div className="text-xs mt-1">
                {es ? "El equipo de IQES recibió tu respuesta." : "The IQES team has received your response."}
              </div>
            </div>
          )}

          {/* Feedback log */}
          {feedback.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="public-feedback-log">
              <h4 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">
                {es ? "Historial" : "History"}
              </h4>
              <ul className="space-y-3">
                {feedback.map(f => (
                  <li key={f.id} className="text-xs border-l-2 pl-3 border-slate-200">
                    <div className="flex items-center gap-1.5">
                      {f.action === "approve"
                        ? <CheckCircle2 size={12} className="text-emerald-600" />
                        : <MessageSquare size={12} className="text-[#003366]" />}
                      <span className="font-medium text-slate-800">{f.signatory_name || "—"}</span>
                      <span className="font-mono text-slate-400 ml-auto">
                        {new Date(f.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {f.message && <div className="mt-1 text-slate-600">{f.message}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 text-center">
            © 2026 IQES LLC · Secure signed link
          </div>
        </aside>
      </div>
    </div>
  );
}
